// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import CustomFieldInput from '../../components/CustomFieldInput'
import {
  ArrowLeft, Pill, User, Stethoscope, Receipt, Activity,
  AlertTriangle, ClipboardList, Package, TrendingUp, Eye, EyeOff,
} from 'lucide-react'
import * as api from '../../api/client'
import CategoryBadge from '../../components/common/CategoryBadge'
import { friendlyError } from '../../api/errors'
import { formatNpi, formatDob, formatUsDate, formatUsDateTime, formatNdc, formatPhone, formatRxOrigin } from '../../api/format'
import { ClinicalStatePill, PayerStatePill, ErrorBanner, LoadingState, formatTimestamp } from '../../components/workflow/StatePills'
import { ShipStatusPill } from '../../components/workflow/ShipStatusPill'
import WorkBenchSection from '../../components/workflow/WorkBench'
import { useModules } from '../../auth/ModulesContext'
import RxComments from '../../components/workflow/RxComments'
import RxDelivery from '../../components/workflow/RxDelivery'
import RxShippingSection from './RxShippingSection'
import { useAuth } from '../../auth/AuthContext'
import PioneerRxSourceBanner from '../../components/workflow/PioneerRxSourceBanner'
import { usePmsName } from '../../auth/AuthContext'
import { useRxSiteOfCare } from '../../hooks/queries/siteOfCare'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

/**
 * /rx/:id — Full Rx record with every PioneerRx-mapped field
 * exposed for pharmacy_staff. Sectioned layout:
 *
 *   1. Header — patient + Rx number + state pills
 *   2. Drug + sig
 *   3. Prescribed vs dispensed (NDC, GCN, lot, etc.)
 *   4. PioneerRx status (verbatim from PMS)
 *   5. Patient detail
 *   6. Prescriber detail
 *   7. Financials (ingredient cost, dispensing fee, patient paid)
 *   8. Claims (adjudication)
 *   9. Fills history
 *  10. State log (audit trail)
 *  11. Comments + alerts
 */
export default function RxDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [sp] = useSearchParams()
  const fromShipmentId = sp.get('from') === 'shipment' ? sp.get('sid') : null
  const { role, hasPermission, me } = useAuth()
  const { isDisabled: isModuleDisabled } = useModules()
  const isProvider = role === 'provider' || role === 'provider_nurse'

  const [data, setData] = useState<api.RxFullResponse | null>(null)
  // Delivery status chip in the header (Patrick 2026-07-16); Ref#-aware lookup.
  const [headShip, setHeadShip] = useState<{ id?: string; status: string; delivered_at?: string | null; shipped_at?: string | null } | null>(null)
  // D-SHIP-11 (Patrick 2026-08-02): delivery events merged into the Rx timeline.
  const [shipEvents, setShipEvents] = useState<api.ShipmentEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [masked, setMasked] = useState(false)  // "hide PHI" toggle — server re-redacts when on
  // Per-pharmacy saved layout. When present (source==='custom') the grid renders from it;
  // otherwise the built-in hardcoded view below is used unchanged.
  const [layout, setLayout] = useState<api.RxViewLayout | null>(null)
  const [cfv, setCfv] = useState<api.CustomFieldValues | null>(null)
  // D-PMS-1: the vendor's own name, so this page never asserts PioneerRx to a
  // pharmacy running something else.
  const { name: pmsName } = usePmsName()
  // D-SOC-2: page-level site-of-care query so a CONFIGURED layout (renderLayoutGrid) can resolve the
  // rx_site_of_care.* fields the builder offers. Unconditional hook — above every early return.
  const socQ = useRxSiteOfCare(id)
  useEffect(() => { api.getRxViewLayout().then(setLayout).catch(() => setLayout(null)) }, [])
  useEffect(() => { if (id) api.getCustomFieldValues('rx', id).then(setCfv).catch(() => setCfv(null)) }, [id])
  useEffect(() => {
    if (!id) return
    api.getRxShipment(id).then((r: any) => {
      const s = r?.shipment
      setHeadShip(s && s.status !== 'cancelled' ? { id: s.id, status: s.status, delivered_at: s.delivered_at, shipped_at: s.shipped_at } : null)
      setShipEvents(s && s.status !== 'cancelled' ? (r.events ?? []) : [])
    }).catch(() => { setHeadShip(null); setShipEvents([]) })
  }, [id])
  // D-DRUGREF-3: per-Rx DrugReference field values, fetched separately so the core Rx query is
  // untouched. Only when the drugreference module is on (BioAccess never fetches or renders these).
  const drugRefOn = !isModuleDisabled('drugreference')
  const [drugRef, setDrugRef] = useState<api.RxDrugRefFields | null>(null)
  useEffect(() => { if (id && drugRefOn) api.getDrugRefForRx(id).then(setDrugRef).catch(() => setDrugRef(null)) }, [id, drugRefOn])

  // D-FILL-1 WS5 slice 1: the fills of this prescription, each its own Rx.
  // Fetched separately so the core Rx query is untouched — if this errors or
  // comes back empty the page renders exactly as it does today. Empty is the
  // NORMAL case right now: fill pages only exist for envelopes received since
  // migration 285 landed, and the historical backfill has not run.
  const [fillPages, setFillPages] = useState<api.RxFillPage[]>([])
  useEffect(() => {
    if (!id) { setFillPages([]); return }
    api.getRxFillPages(id).then(r => setFillPages(r.fill_pages ?? [])).catch(() => setFillPages([]))
  }, [id])

  useEffect(() => {
    if (!id) return
    setLoading(true); setError(null)
    api.getRxFull(id, masked)
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [id, masked])

  if (loading) return <LoadingState label="Loading prescription record…" />
  if (error)   return <ErrorBanner message={error} />
  if (!data)   return null

  const { rx, patient, prescriber, dispensed, claims, employees, fills, state_log, workbench_log,
          dispensed_detail, prescribed_detail, pricing, pay_methods, detail_claims,
          patient_phones, patient_addresses, financial_visible } = data as any

  // E-CV-203: single chronological stream (newest first) of PMS + Workbench entries.
  // PLAIN computation, NOT a hook: this sits below the loading/error early returns,
  // and a hook here changes the hook count between renders — React unmounts the tree
  // (the 2026-07-29 blank-detail-page bug). A few hundred entries sort trivially per render.
  type WbEntry = { field: string; from: string | null; to: string | null; actor_account: string | null; actor_role: string | null; at: string }
  const mergedTimeline = (() => {
    const pms = (state_log ?? []).map((p: any) => ({ kind: 'pms' as const, at: p.transitioned_at as string, pms: p, wb: undefined as WbEntry | undefined, ship: undefined as any }))
    const wb = ((workbench_log ?? []) as WbEntry[]).map((w: WbEntry) => ({ kind: 'wb' as const, at: w.at, pms: undefined as any, wb: w, ship: undefined as any }))
    // D-SHIP-11: delivery leg in the SAME stream — Rx workflow + shipping, A to Z.
    const ship = shipEvents
      .filter(e => e.occurred_at)
      .map(e => ({ kind: 'ship' as const, at: e.occurred_at, pms: undefined as any, wb: undefined as WbEntry | undefined, ship: e }))
    if (headShip?.shipped_at && !shipEvents.some(e => e.status === 'label_purchased' || Math.abs(Date.parse(e.occurred_at) - Date.parse(headShip.shipped_at!)) < 120000))
      ship.push({ kind: 'ship', at: headShip.shipped_at, pms: undefined as any, wb: undefined, ship: { status: 'label_purchased', carrier_status_text: 'Shipped — label purchased', description: null, location: null, occurred_at: headShip.shipped_at } })
    return [...pms, ...wb, ...ship].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
  })()
  // D-SHIP-11: A→Z — first recorded activity to delivery.
  const azLabel = (() => {
    if (!headShip?.delivered_at || mergedTimeline.length === 0) return null
    const oldest = mergedTimeline[mergedTimeline.length - 1].at
    const ms = Date.parse(headShip.delivered_at) - Date.parse(oldest)
    if (!isFinite(ms) || ms <= 0) return null
    const d = Math.floor(ms / 86400000), h = Math.round((ms % 86400000) / 3600000)
    return d > 0 ? `${d}d ${h}h` : `${h}h`
  })()
  // Sprint 15: prefer projection-table data over legacy `dispensed[0]`
  const dispensedRow = dispensed_detail ?? dispensed?.[0]
  const primaryAddress = patient_addresses?.[0]
  const primaryPhone = patient_phones?.[0]
  void prescribed_detail; void pricing; void pay_methods; void detail_claims  // available for future sections
  const empName = (eid: any) =>
    eid && typeof eid === 'string' && employees[eid] ? employees[eid].display_name : null

  // ---- layout-driven rendering (opt-in per pharmacy) ----
  // Values come from the already role-redacted rx/patient/prescriber objects, so RBAC masking
  // is respected; the layout only decides which cards/fields show, their labels, and order.
  const layoutFieldValue = (f: any): React.ReactNode => {
    const col = f.owlrx_column
    // Composite / derived fields, rendered to match the canonical hardcoded view so a
    // saved layout reproduces exactly what the pharmacy saw before customizing.
    if (f.owlrx_table === 'rx_items') {
      // D-FILL-7: a CONFIGURED layout renders through here, and it reads rx.* —
      // which is the PRESCRIPTION mirror. On a fill page that showed 52849 while
      // the API had already returned fill_view.display_rx_number = '52849-1'.
      // Every fill-identity fix so far landed on the FALLBACK header, which a
      // pharmacy with a saved layout (ASP) never renders. Resolve the fill's own
      // values here too, so configured and default headers agree.
      const fvHdr: any = (data as any)?.fill_view
      if (fvHdr) {
        if (col === 'rx_number') return (fvHdr.display_rx_number as string) ?? (rx.rx_number as string) ?? '—'
        if (col === 'refill_number') return fvHdr.fill_number == null ? '—' : String(fvHdr.fill_number)
        if (col === 'state') return (fvHdr.state as string) ?? (rx.state as string) ?? '—'
        if (col === 'pioneerrx_rx_status') return (fvHdr.pioneerrx_rx_status as string) ?? (rx.pioneerrx_rx_status as string) ?? '—'
        if (col === 'pioneerrx_tx_status') return (fvHdr.pioneerrx_tx_status as string) ?? (rx.pioneerrx_tx_status as string) ?? '—'
        if (col === 'date_filled_utc' && fvHdr.date_filled_utc)
          return new Date(fvHdr.date_filled_utc as string).toLocaleDateString()
      }
      if (col === 'drug_name') return (rx.prescribed_written_name as string) ?? (rx.drug_name as string) ?? '—'
      if (col === 'sig') return (rx.sig_code as string) ?? (rx.sig as string) ?? '—'
      if (col === 'quantity_authorized')
        return rx.quantity_authorized == null ? '—' : `${rx.quantity_authorized}${rx.quantity_unit ? ` ${rx.quantity_unit}` : ''}`
      if (col === 'refills_authorized')
        return rx.refills_authorized == null ? '—' : `${rx.refills_remaining ?? rx.refills_authorized} of ${rx.refills_authorized}`
      if (/_employee_id$/.test(col)) return empName((rx as any)[col]) ?? '—'
      // 'Prescriber — —' bug (Patrick 2026-07-16): layouts may map the prescriber
      // as rx_items.prescriber_name (a LIST-payload alias, not a real rx_items
      // column) — resolve it from the loaded prescriber record.
      if (col === 'prescriber_name' || col === 'prescriber')
        return (rx as any).prescriber_name ?? (prescriber.display_name as string) ?? '—'
    }
    if (f.owlrx_table === 'patients') {
      if (col === 'display_name') return (patient.display_name as string) ?? '—'
      if (col === 'gender') return (patient.gender as string) ?? (patient.sex as string) ?? '—'
      if (col === 'primary_phone')
        return formatPhone((patient.primary_phone as string) ?? (primaryPhone?.formatted_number as string) ?? (primaryPhone?.number as string))
      if (col === 'address') {
        const a = primaryAddress
          ? [primaryAddress.address_line, primaryAddress.city, primaryAddress.state_code, primaryAddress.zip_code]
          : [patient.address_line, patient.city, patient.state_code, patient.postal_code]
        return a.filter(Boolean).join(', ') || '—'
      }
      if (col === 'allergies') return (patient.allergies as string) ?? (patient.allergy_alert_type as string) ?? 'None on file'
    }
    // D-SOC-2: virtual table backed by GET /rx/{id}/site-of-care (hidden fields never reach here:
    // the builder + field_catalog decide what is offered; the value is business data, not PHI).
    if (f.owlrx_table === 'rx_site_of_care') {
      const soc = socQ.data?.site_of_care
      if (!soc) return socQ.isLoading ? '…' : '—'
      if (col === 'facility_type') return soc.facility_type_label.split(' — ')[0]
      if (col === 'facility_name') return soc.facility_name ?? (soc.facility_type === 'residential' ? 'Patient residence' : '—')
      if (col === 'facility_phone') return formatPhone(soc.facility_phone)
      if (col === 'match_method') return ({ address: 'Address match', address_loose: 'Near match', manual: 'Pinned by pharmacy', none: 'No match' } as Record<string, string>)[soc.match_method] ?? soc.match_method
      if (col === 'contact_name') return soc.contact_name ?? '—'
      if (col === 'contact_phone') return formatPhone(soc.contact_phone)
      if (col === 'contact_email') return soc.contact_email ?? '—'
      if (col === 'operating_hours') return soc.operating_hours ?? '—'
      if (col === 'delivery_cutoff_time') return soc.delivery_cutoff_time ?? '—'
      return '—'
    }
    if (f.owlrx_table === 'prescribers') {
      if (col === 'npi') return formatNpi(prescriber.npi as string) || '—'
      if (col === 'address')
        return [prescriber.address_line, prescriber.city, prescriber.state_code, prescriber.postal_code].filter(Boolean).join(', ') || '—'
    }
    const src: any = f.owlrx_table === 'patients' ? patient : f.owlrx_table === 'prescribers' ? prescriber : rx
    if (f.owlrx_table === 'drug_reference') {
      if (f.owlrx_column === 'category') return <CategoryBadge name={drugRef?.category} color={drugRef?.category_color} />
      const dv = (drugRef as any)?.[f.owlrx_column]
      return dv == null || dv === '' ? '—' : String(dv)
    }
    let v = src?.[f.owlrx_column]
    if (v == null || v === '') return '—'
    v = String(v)
    // ASP-62/63: ASP renders through THIS layout path (saved custom layout), not the
    // hardcoded fields — NDC 5-4-2 and phone/fax formatting must live here.
    // ASP 2026-09-28: the Origin field still read "3" on the detail card after formatRxOrigin
    // shipped, because a pharmacy with a SAVED layout renders through this path and the fix had
    // only reached the hardcoded fallback section. Same rule as NDC and phone above: formatting
    // that matters to a pharmacist must live on the path the pharmacy actually renders through.
    if (f.owlrx_column === 'origin_type') v = formatRxOrigin(v)
    else if (/ndc/i.test(f.owlrx_column)) v = formatNdc(v)
    else if (/phone|fax/i.test(f.owlrx_column)) v = formatPhone(v)
    else if (/dob|birth/i.test(f.owlrx_column)) v = formatDob(v)
    else if (/date|expiration|_at$|_utc$/i.test(f.owlrx_column) && /^\d{4}-\d{2}-\d{2}/.test(v)) v = formatUsDateTime(v)
    // Money, to match the hardcoded <Money> renderer. A saved layout renders
    // through here, and this branch was missing — so every currency field showed
    // a raw float (67.367, 1.5, 99.1822) instead of $67.37. Checked AFTER the
    // date branch so a column like paid_at is still treated as a date, and guarded
    // on the value being numeric so a text column named ...amount is left alone.
    else if (/(_cost|_price|_fee|_charge|copay|amount|_paid)/i.test(f.owlrx_column)) {
      const money = Number(v)
      if (v.trim() !== '' && Number.isFinite(money)) v = '$' + money.toFixed(2)
    }
    return v
  }
  // A saved layout's title can name the vendor the pharmacy USED to be assumed to
  // run. Swap in the tenant's actual dispensing system rather than editing rows.
  const vendorTitle = (title: string) =>
    typeof title === 'string' ? title.replace(/PioneerRx/gi, pmsName) : title
  const cardIcon = (key: string): any =>
    ({ drug: Pill, dispensed: Package, patient: User, prescriber: Stethoscope, pioneerrx: Activity, financials: Receipt, team: ClipboardList, comments: AlertTriangle } as any)[key] ?? Pill
  const myRoles = new Set(me?.authenticated ? (me.role_ids?.length ? me.role_ids : (me.primary_role_id ? [me.primary_role_id] : [])) : [])
  const isPlatformAdmin = hasPermission('platform.access')
  // Card-level role gate shared by the card grid and the promoted sections below.
  // DENY wins over allow; platform admins always see everything.
  const roleHidesCard = (card: any): boolean => {
    if (isPlatformAdmin) return false
    if (Array.isArray(card?.denied_roles) && card.denied_roles.some((r: string) => myRoles.has(r))) return true
    if (Array.isArray(card?.allowed_roles) && card.allowed_roles.length > 0
        && !card.allowed_roles.some((r: string) => myRoles.has(r))) return true
    return false
  }
  // The promoted sections (delivery/claims/fills/clinical_comments/timeline) render OUTSIDE the card
  // grid. They become builder-controllable only when this pharmacy has a CUSTOM layout that carries the
  // card; otherwise we fall back to the legacy unconditional render so default pharmacies are unchanged.
  const specialCard = (key: string): any =>
    (layout?.source === 'custom' ? (layout.cards ?? []).find((c: any) => c.card_key === key) : undefined)
  // 'legacy' = render exactly as before; 'show'/'hide' = honor the card's visible + role config.
  const specialCardGate = (key: string): 'show' | 'hide' | 'legacy' => {
    const card = specialCard(key)
    if (!card) return 'legacy'
    if (!card.visible) return 'hide'
    return roleHidesCard(card) ? 'hide' : 'show'
  }
  const regionCards = (region: string) => (layout?.cards ?? []).filter((c: any) => (c?.config?.region ?? 'body') === region)
  const bandAllow = api.headerBandAllow(layout?.cards as any)
  const showCore = (k: string) => !bandAllow || bandAllow.includes(k)
  function renderLayoutGrid(cardsList: any[]) {
    if (!layout) return null
    return cardsList.map((card: any) => {
      if (!card.visible) return null
      // Card-level role gate: deny wins over allow; empty/absent allow = visible to all.
      // Platform admins always see every card.
      if (roleHidesCard(card)) return null
      if (card.kind === 'workbench')
        return (id && !isModuleDisabled('queues')) ? <WorkBenchSection key={card.card_key} rxId={id} canEdit={true} /> : null
      if (card.kind === 'financials' && financial_visible === false) return null
      const vis = (card.fields ?? []).filter((f: any) => f.visible)
      if (!vis.length) return null
      const Icon = cardIcon(card.card_key)
      return (
        <Section key={card.card_key} icon={Icon} title={vendorTitle(card.title)}>
          {vis.map((f: any) => <Field key={`${f.owlrx_table}.${f.owlrx_column}`} label={f.owlrx_label}>{(f.owlrx_table === 'custom') ? <CustomFieldInput surface="rx" recordId={id || ''} cardKey={card.card_key} field={f} initialValue={cfv?.values?.[`${card.card_key}.${f.owlrx_column}`]} canWrite={!!cfv?.can_write} canModify={!!cfv?.can_modify} /> : layoutFieldValue(f)}</Field>)}
          {/* D-SOC-1: a pharmacy with a SAVED layout (CDI, ASP) renders its Patient card through here, not the
              static section below — the site-of-care rows ride on whichever card carries the patient fields. */}
          {id && (card.card_key === 'patient' || vis.some((f: any) => f.owlrx_table === 'patients')) && <RxSiteOfCareFields rxId={id} />}
        </Section>
      )
    })
  }

  // Customized Rx header rendered band-style (inner content; the headerCard
  // container + functional pills/nav buttons stay outside).
  const renderHeaderBand = (headerCards: any[]) => {
    const fs = (headerCards || []).filter((c: any) => c.visible).flatMap((c: any) => (c.fields ?? []).filter((f: any) => f.visible))
    const nameF = fs.find((f: any) => f.owlrx_column === 'display_name')
    const meta = fs.filter((f: any) => !['display_name', 'mrn'].includes(f.owlrx_column))
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={titleStyle}>{nameF ? layoutFieldValue(nameF) : (patient.display_name as string)}</h1>
          {rx.is_controlled && <span style={controlledBadge}>{(rx.prescribed_dea_schedule as string) ?? 'CTRL'}</span>}
        </div>
        {meta.length > 0 && (
          <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: 'var(--color-text-soft)', flexWrap: 'wrap' }}>
            {meta.map((f: any) => <span key={f.owlrx_column}><strong style={{ color: 'var(--color-text)' }}>{f.owlrx_label}</strong> {layoutFieldValue(f)}</span>)}
          </div>
        )}
      </>
    )
  }

  return (
    <div>
      <button onClick={() => navigate(-1)} style={backBtn}>
        <ArrowLeft size={14} aria-hidden="true" /> Back
      </button>

      <PioneerRxSourceBanner variant="detail" />

      {/* PHI mask/unmask toggle — only PHI-authorized roles can reveal; hiding re-fetches a masked payload */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        {data.phi_access ? (
          <button onClick={() => setMasked(m => !m)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            title={masked ? 'Reveal PHI' : 'Hide PHI for privacy / screen-sharing'}>
            {masked ? <><Eye size={14} aria-hidden="true" /> Show PHI</> : <><EyeOff size={14} aria-hidden="true" /> Hide PHI</>}
          </button>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-mute)' }}
            title="PHI is masked for your role">
            <EyeOff size={14} aria-hidden="true" /> PHI masked — your role can't reveal it
          </span>
        )}
      </div>

      {/* Header */}
      <div style={headerCard}>
        {regionCards('header').length > 0 ? (
          renderHeaderBand(regionCards('header'))
        ) : (<>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={titleStyle}>{patient.display_name as string}</h1>
          {rx.is_controlled && <span style={controlledBadge}>{(rx.prescribed_dea_schedule as string) ?? 'CTRL'}</span>}
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: 'var(--color-text-soft)', flexWrap: 'wrap' }}>
          {showCore('rx_number') && <span><strong style={{ color: 'var(--color-text)' }}>Rx #</strong> {((data as any)?.fill_view?.display_rx_number as string) ?? (rx.rx_number as string) ?? '—'}</span>}
          {/* D-FILL-5: the PRESCRIPTION page and a FILL page were visually
              identical — rx_items mirrors the LATEST fill, so the parent showed
              "Rx # 100339  Refill 2", which reads exactly like the page for
              100339-2 and is why 100339-2 looked broken when it was not.
              A refill number is a property of a FILL, not of the prescription, so
              the parent no longer claims one; it points at the fill instead. */}
          {showCore('refill_number') && (data as any)?.fill_view && ((data as any).fill_view.fill_number as number) > 0 && (
            <span><strong style={{ color: 'var(--color-text)' }}>Refill</strong> {String((data as any).fill_view.fill_number)}</span>
          )}
          {/* D-WBFILL-3 §2.6: D-FILL-5 named the parent and every refill, but left fill 0 — whose
              display key IS the bare Rx number — with no marker at all, so it read as "the Rx".
              That is the page Rx 104750's misdirected WorkBench edits were made on. */}
          {showCore('refill_number') && (data as any)?.fill_view && ((data as any).fill_view.fill_number as number) === 0 && (
            <span style={{ color: 'var(--color-text-mute)' }}>
              <strong style={{ color: 'var(--color-text)' }}>Original fill</strong>
              {(rx.refill_number as number) > 0 ? <> — prescription is now on refill {String(rx.refill_number)}</> : null}
            </span>
          )}
          {!(data as any)?.fill_view && (rx.refill_number as number) > 0 && (
            <span style={{ color: 'var(--color-text-mute)' }}>
              <strong style={{ color: 'var(--color-text)' }}>Prescription</strong> — latest fill{' '}
              <strong>{`${rx.rx_number}-${rx.refill_number}`}</strong>
            </span>
          )}
          {showCore('prescriber') && <span><strong style={{ color: 'var(--color-text)' }}>Prescriber</strong> {prescriber.display_name as string}</span>}
        </div>
        </>)}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <ClinicalStatePill state={rx.state as string} />
          {/* Raw PMS status is pharmacy-internal: the provider portal shows DELIVERY as the
              status instead (ShipStatusPill below), so this chip is staff-only. */}
          {!isProvider && rx.pioneerrx_rx_status && (
            <span style={originalStatusStyle}>
              {pmsName}: {rx.pioneerrx_rx_status as string}
            </span>
          )}
          <PayerStatePill state={rx.payer_state as string} />
          {/* Providers get the one delivery chip, clickable in every state — the same POD page
              the shipping drawer's "Download POD" opens. Staff keep the existing chips. */}
          {isProvider && headShip && id && <ShipStatusPill rxId={id} status={headShip.status} deliveredAt={headShip.delivered_at} />}
          {!isProvider && headShip?.status === 'delivered' && (
            <span title="Download proof of delivery"
                  onClick={() => { if (id) { api.openRxPodDoc(id).catch(() => { alert('Could not open the delivery document. Please try again.') }) } }}
                  style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: '#dcfce7', color: '#166534', cursor: 'pointer', textDecoration: 'underline dotted' }}>
              ✓ Delivered{headShip.delivered_at ? ` ${new Date(headShip.delivered_at).toLocaleDateString()}` : ''} · POD ↗
            </span>
          )}
          {azLabel && (
            <span title="Total time from first recorded activity to delivery"
                  style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'var(--color-surface-2, #f1f5f9)', color: 'var(--color-text-soft, #475569)', border: '1px solid var(--color-border)' }}>
              A→Z {azLabel}
            </span>
          )}
          {!isProvider && headShip && ['in_transit', 'out_for_delivery', 'label_purchased', 'created', 'picked_up'].includes(headShip.status) && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: '#dbeafe', color: '#1e40af' }}>⛟ In transit</span>
          )}
          {!isProvider && headShip?.status === 'exception' && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: '#fee2e2', color: '#991b1b' }}>⚠ Delivery issue</span>
          )}
          {!isProvider ? (
            <>
              <button
                type="button"
                onClick={() => navigate(`/patients/${patient.id as string}`)}
                style={openLinkBtn}
              >
                View patient profile →
              </button>
              <button
                type="button"
                onClick={() => navigate(`/prescribers/${prescriber.id as string}`)}
                style={openLinkBtn}
              >
                View prescriber →
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => navigate(`/provider/patients/${patient.id as string}`)}
              style={openLinkBtn}
            >
              View patient profile →
            </button>
          )}
        </div>
      </div>



      {/* Two-column grid */}
      <div style={gridStyle}>
        {layout?.source === 'custom' ? renderLayoutGrid(regionCards('body')) : (<>

        {/* Drug + sig */}
        <Section icon={Pill} title="Drug & directions">
          <Field label="Prescribed">{(rx.prescribed_written_name as string) ?? (rx.drug_name as string)}</Field>
          <Field label="Strength">{rx.drug_strength as string}</Field>
          <Field label="Form">{rx.drug_form as string}</Field>
          <Field label="Sig">{(rx.sig_code as string) ?? (rx.sig as string)}</Field>
          {rx.directions_translated && <Field label="Directions">{rx.directions_translated as string}</Field>}
          <Field label="Quantity">{`${rx.quantity_authorized} ${rx.quantity_unit}`}</Field>
          {rx.days_supply != null && <Field label="Days supply">{String(rx.days_supply)}</Field>}
          <Field label="Refills">{`${rx.refills_remaining ?? rx.refills_authorized} of ${rx.refills_authorized}`}</Field>
          {rx.daw_text && <Field label="DAW">{`${rx.daw_code} — ${rx.daw_text}`}</Field>}
          {rx.is_prn && <Field label="PRN">Yes</Field>}
          {rx.allow_partial_fills && <Field label="Partial fills">Allowed</Field>}
        </Section>

        {/* Shipping (Sprint 18) — renders only for shippable/shipped states + shipping.read */}
        {fromShipmentId && (
          <button type="button"
                  onClick={() => navigate(`/shipments?shipment=${fromShipmentId}`)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12,
                           padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border)',
                           background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 12.5,
                           fontWeight: 600, cursor: 'pointer' }}>
            ← Back to shipment
          </button>
        )}
        <RxShippingSection rxId={rx.id as string} rxState={rx.state as string} />

        {/* Prescribed vs dispensed (NDC etc.) */}
        <Section icon={Package} title="Prescribed → dispensed">
          <Field label="Prescribed NDC">{formatNdc(rx.prescribed_ndc as string)}</Field>
          <Field label="Prescribed GCN">{(rx.prescribed_gcn as string) ?? '—'}</Field>
          <Field label="Prescribed brand">{(rx.prescribed_brand_name as string) ?? '—'}</Field>
          <Field label="Prescribed generic">{(rx.prescribed_generic_name as string) ?? '—'}</Field>
          <hr style={hrStyle} />
          {dispensedRow ? (
            <>
              <Field label="Dispensed drug">{dispensedRow.drug_name as string}</Field>
              <Field label="Dispensed NDC">{formatNdc(dispensedRow.ndc as string)}</Field>
              <Field label="Manufacturer">{(dispensedRow.manufacturer as string) ?? '—'}</Field>
              <Field label="Lot number">{(dispensedRow.lot_number as string) ?? '—'}</Field>
              {dispensedRow.lot_expiration_date && (
                <Field label="Lot expiration">{formatUsDate(dispensedRow.lot_expiration_date as string)}</Field>
              )}
              {dispensedRow.substitution_text && (
                <Field label="Substitution">{dispensedRow.substitution_text as string}</Field>
              )}
            </>
          ) : (
            <span style={mutedSm}>No dispensed record yet.</span>
          )}
        </Section>

        {/* Patient */}
        <Section icon={User} title="Patient">
          <Field label="Name">{patient.display_name as string}</Field>
          <Field label="Date of birth">{formatDob(patient.date_of_birth as string)}</Field>
          <Field label="Gender">{(patient.gender as string) ?? (patient.sex as string) ?? '—'}</Field>
          <Field label="Phone">{formatPhone((patient.primary_phone as string) ?? (primaryPhone?.formatted_number as string) ?? (primaryPhone?.number as string))}</Field>
          <Field label="Email">{(patient.email as string) ?? '—'}</Field>
          <Field label="Address">{
            primaryAddress
              ? [primaryAddress.address_line, primaryAddress.city, primaryAddress.state_code, primaryAddress.zip_code]
                  .filter(Boolean).join(', ')
              : [patient.address_line, patient.city, patient.state_code, patient.postal_code]
                  .filter(Boolean).join(', ') || '—'
          }</Field>
          <Field label="Allergies">{(patient.allergies as string) ?? (patient.allergy_alert_type as string) ?? 'None on file'}</Field>
          {patient.allergy_alert_type && patient.allergies && (
            <Field label="Allergy alert">{patient.allergy_alert_type as string}</Field>
          )}
          {patient.language_text && <Field label="Language">{patient.language_text as string}</Field>}
          {patient.delivery_method && <Field label="Delivery preference">{patient.delivery_method as string}</Field>}
          {/* D-SOC-1: where this Rx is delivered — a Site of Care facility or the patient's residence. */}
          {id && <RxSiteOfCareFields rxId={id} />}
          {patient.is_animal && <Field label="Patient type">Veterinary</Field>}
          {patient.height_inches !== null && patient.height_inches !== undefined && (
            <Field label="Height">{String(patient.height_inches)} in</Field>
          )}
          {patient.weight_ounces !== null && patient.weight_ounces !== undefined && (
            <Field label="Weight">{(Number(patient.weight_ounces) / 16).toFixed(1)} lb</Field>
          )}
        </Section>

        {/* Prescriber */}
        <Section icon={Stethoscope} title="Prescriber">
          <Field label="Name">{prescriber.display_name as string}</Field>
          <Field label="NPI">{formatNpi(prescriber.npi as string)}</Field>
          <Field label="DEA">{(prescriber.dea_number as string) ?? '—'}</Field>
          <Field label="Phone">{formatPhone(prescriber.primary_phone as string)}</Field>
          <Field label="Fax">{formatPhone(prescriber.primary_fax as string)}</Field>
          <Field label="Email">{(prescriber.email as string) ?? '—'}</Field>
          <Field label="Address">{
            [prescriber.address_line, prescriber.city, prescriber.state_code, prescriber.postal_code]
              .filter(Boolean).join(', ') || '—'
          }</Field>
        </Section>

        {/* WorkBench / Operations — ASP workflow status fields (color-coded, editable) */}
        {id && !isModuleDisabled('queues') && <WorkBenchSection rxId={id} canEdit={true} />}

        {/* PMS status — pharmacy-internal, hidden from the provider portal. */}
        {!isProvider && <Section icon={Activity} title={`${pmsName} status (verbatim)`}>
          <Field label="PMS Rx status">{(rx.pioneerrx_rx_status as string) ?? '—'}</Field>
          <Field label="PMS Tx status">{(rx.pioneerrx_tx_status as string) ?? '—'}</Field>
          <Field label="Origin">{formatRxOrigin(rx.origin_type)}</Field>
          <Field label="Date written">{formatUsDate(rx.date_written as string)}</Field>
          <Field label="Date entered">{formatUsDateTime(rx.date_entered as string)}</Field>
          <Field label="Date filled">{formatUsDateTime(rx.date_filled_utc as string)}</Field>
          <Field label="Expiration">{formatUsDate(rx.expiration_date_utc as string)}</Field>
          <Field label="Promise time">{formatUsDateTime(rx.promise_time_utc as string)}</Field>
          <Field label="Fill request">{(rx.fill_request_method as string) ?? '—'}</Field>
          <Field label="Drug use">{(rx.drug_use_type as string) ?? '—'}</Field>
        </Section>}

        {/* Financials — gated: only roles with rx.read_financial (e.g. pharmacist,
            manager, owner, biller) see cost/price data; technicians do not. */}
        {financial_visible === false ? (
          <Section icon={Receipt} title="Financials">
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)', padding: '4px 0' }}>
              Financial details are hidden for your role.
            </div>
          </Section>
        ) : (
          <Section icon={Receipt} title="Financials">
            <Money label="Ingredient cost (submitted)" v={rx.ingredient_cost_submitted} />
            <Money label="Ingredient cost (paid)" v={rx.ingredient_cost_paid} />
            <Money label="Dispensing fee (submitted)" v={rx.dispensing_fee_submitted} />
            <Money label="Dispensing fee (paid)" v={rx.dispensing_fee_paid} />
            <Money label="Total price (submitted)" v={rx.total_price_submitted} />
            <Money label="Total price (paid)" v={rx.total_price_paid} />
            <Money label="Patient paid" v={rx.patient_paid_amount} />
            <Money label="Usual & customary" v={rx.usual_customary_price} />
            <Money label="Acquisition cost" v={rx.acquisition_cost} />
            {rx.sale_receipt_number && (
              <Field label="Sale receipt #">{rx.sale_receipt_number as string}</Field>
            )}
          </Section>
        )}

        {/* Profit — computed from financials above; hidden without financial access */}
        {financial_visible !== false && <ProfitSection rx={rx} />}

        {/* Pharmacy team */}
        <Section icon={ClipboardList} title="Pharmacy team">
          <Field label="Data entry">{empName(rx.data_entry_employee_id) ?? '—'}</Field>
          <Field label="Pharmacist">{empName(rx.pharmacist_employee_id) ?? '—'}</Field>
          <Field label="Checked by">{empName(rx.checked_by_pharmacist_employee_id) ?? '—'}</Field>
          <Field label="Filled by">{empName(rx.filled_by_employee_id) ?? '—'}</Field>
          <Field label="Sale completed by">{empName(rx.sale_completed_by_employee_id) ?? '—'}</Field>
        </Section>

        {/* Comments + alerts */}
        <Section icon={AlertTriangle} title="Comments & alerts">
          {rx.rx_comment_critical && (
            <div style={{ ...alertBoxStyle, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}>
              <strong>Critical:</strong> {rx.rx_comment_critical as string}
            </div>
          )}
          {rx.fill_comment_critical && (
            <div style={{ ...alertBoxStyle, background: '#fef3c7', borderColor: '#fcd34d', color: '#854d0e' }}>
              <strong>Fill alert:</strong> {rx.fill_comment_critical as string}
            </div>
          )}
          {rx.rx_comment_informational && (
            <Field label="Note">{rx.rx_comment_informational as string}</Field>
          )}
          {rx.rx_comment_pos && <Field label="POS note">{rx.rx_comment_pos as string}</Field>}
          {patient.comment_critical && (
            <div style={{ ...alertBoxStyle, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}>
              <strong>Patient critical:</strong> {patient.comment_critical as string}
            </div>
          )}
          {patient.comment_informational && (
            <Field label="Patient note">{patient.comment_informational as string}</Field>
          )}
          {!rx.rx_comment_critical && !rx.fill_comment_critical &&
           !rx.rx_comment_informational && !rx.rx_comment_pos &&
           !patient.comment_critical && !patient.comment_informational && (
            <span style={mutedSm}>No comments or alerts on this Rx.</span>
          )}
        </Section>
        </>)}
      </div>

      {layout?.source === 'custom' && regionCards('footer').length > 0 && (
        <div style={gridStyle}>{renderLayoutGrid(regionCards('footer'))}</div>
      )}

      {/* Full-width sections below grid */}

      {specialCardGate('delivery') !== 'hide' && !isModuleDisabled('shipping') && (
        <RxDelivery rxId={rx.id as string} />
      )}

      {/* Claims */}
      {/* Comment thread (provider <-> pharmacy) */}
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        {/* D-FILL-1: on a fill page the comments thread IS the fill's — post and read
            against the fill guid (the URL id); the backend resolves the parent and
            stamps the fill. Prescription pages keep the parent thread. */}
        <RxComments rxId={((data as any)?.fill_view && id) ? (id as string) : (rx.id as string)} payerState={rx.payer_state as string} />
      </div>

      {specialCardGate('claims') !== 'hide' && (<>
      <h2 style={sectionH2}>Claims</h2>
      <div style={cardStyle}>
        {claims.length === 0 ? (
          <div style={{ padding: '24px', color: 'var(--color-text-mute)', fontSize: 13 }}>No claims submitted.</div>
        ) : (
          <RxClaimsTable claims={claims} />
        )}
      </div>
      </>)}

      {/* D-FILL-1: viewing one fill — say so plainly, or the page reads as the
          prescription and the user cannot tell which refill they opened. */}
      {/* D-FILL-6: append ?debug=1 to the URL for a one-line readout of what this
          page actually resolved. Added because three rounds of inference failed to
          explain a header showing the parent number — measuring beats guessing. */}
      {new URLSearchParams(window.location.search).get('debug') === '1' && (
        <div style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 6,
                      background: '#111', color: '#0f0', fontFamily: 'monospace', fontSize: 11 }}>
          url_id={String(id)} · fill_view={(data as any)?.fill_view ? 'PRESENT' : 'NULL'}
          {' '}· display={(data as any)?.fill_view?.display_rx_number ?? '(none)'}
          {' '}· fill_no={String((data as any)?.fill_view?.fill_number ?? '(none)')}
          {' '}· rx.id={String(rx.id)} · rx.rx_number={String(rx.rx_number)}
          {' '}· fill_pages={fillPages.length}
        </div>
      )}

      {(data as any)?.fill_view && (
        <div style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: 8,
                      background: '#ccfbf1', border: '1px solid #99f6e4', fontSize: 13.5 }}>
          Viewing{' '}
          {/* D-WBFILL-3 §2.6: "fill 0" is not language staff use, and on the original fill the
              display number is the bare Rx number, so this banner read as if it were the
              prescription. Name it the way the fills table already does. */}
          {((data as any).fill_view.fill_number as number) === 0
            ? <>the <strong>original fill</strong> <strong>{(data as any).fill_view.display_rx_number}</strong></>
            : <>fill <strong>{(data as any).fill_view.display_rx_number}</strong> (refill {(data as any).fill_view.fill_number})</>}
          {' '}—
          {' '}status <strong>{(data as any).fill_view.pioneerrx_tx_status
                               ?? (data as any).fill_view.state ?? '—'}</strong>
          {(data as any).fill_view.date_filled_utc
            ? `, filled ${new Date((data as any).fill_view.date_filled_utc).toLocaleDateString()}`
            : ''}
        </div>
      )}

      {/* D-FILL-1: fills of this prescription, each as its own Rx. Rendered ONLY
          when pages exist, so a prescription with none looks exactly as before. */}
      {fillPages.length > 0 && (<>
      <h2 style={sectionH2}>Fills of this prescription</h2>
      <div style={cardStyle}>
        <RxFillPagesTable fillPages={fillPages} thisPageRx={(data as any)?.fill_view?.display_rx_number} />
        <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--color-text-mute)' }}>
          Each fill is tracked separately. Fills recorded before OwlRx began receiving
          this pharmacy&rsquo;s prescription feed are not listed.
        </div>
      </div>
      </>)}

      {/* Fills */}
      {specialCardGate('fills') !== 'hide' && (<>
      <h2 style={sectionH2}>Fills</h2>
      <div style={cardStyle}>
        {fills.length === 0 ? (
          <div style={{ padding: '24px', color: 'var(--color-text-mute)', fontSize: 13 }}>No fills recorded.</div>
        ) : (
          <RxFillsTable fills={fills} />
        )}
      </div>
      </>)}

      {/* PioneerRx clinical comments — date-stamped staff notes packed by PioneerRx
          into Body.Rx.Comments.Critical etc. AND patient-level comments from
          Body.Patient.Comments (delivery instructions, patient warnings) which
          apply to every Rx for that patient. */}
      {(() => {
        if (specialCardGate('clinical_comments') === 'hide') return null
        const ccCard = specialCard('clinical_comments')
        const ccTitle = ccCard?.title ?? 'Clinical comments'
        const infoOnly = !!ccCard?.config?.informational_only
        const anyInfo = !!(patient.comment_informational || rx.rx_comment_informational || ((data as any)?.fill_view?.fill_comment_informational ?? rx.fill_comment_informational))
        // D-FILL-2: on a fill page the FILL family comes from that fill; the RX
        // family stays prescription-level (PioneerRx's own split) and shows on
        // every fill. Falls back to rx_items pre-289 / on prescription pages.
        const fv: any = (data as any)?.fill_view
        const fcCrit = fv?.fill_comment_critical      ?? rx.fill_comment_critical
        const fcInfo = fv?.fill_comment_informational ?? rx.fill_comment_informational
        const fcPos  = fv?.fill_comment_pos           ?? rx.fill_comment_pos
        const anyComment = !!(rx.rx_comment_critical || rx.rx_comment_informational || rx.rx_comment_pos
          || fcCrit || fcInfo || fcPos
          || patient.comment_critical || patient.comment_informational || patient.comment_pos)
        if (infoOnly ? !anyInfo : !anyComment) return null
        return (
        <>
          <h2 style={sectionH2}>{ccTitle}</h2>
          <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
            {/* Patient-level shows first because it applies to every Rx for this patient */}
            {!infoOnly && patient.comment_critical && (
              <CommentsPanel
                label="Critical (patient)"
                accent="#dc2626"
                raw={patient.comment_critical as string}
              />
            )}
            {!infoOnly && patient.comment_pos && (
              <CommentsPanel
                label="Delivery instructions (patient)"
                accent="#0891b2"
                raw={patient.comment_pos as string}
              />
            )}
            {patient.comment_informational && (
              <CommentsPanel
                label="Informational (patient)"
                accent="#6b7280"
                raw={patient.comment_informational as string}
              />
            )}
            {!infoOnly && rx.rx_comment_critical && (
              <CommentsPanel
                label="Fill Notes"
                accent="#dc2626"
                raw={rx.rx_comment_critical as string}
              />
            )}
            {!infoOnly && rx.rx_comment_pos && (
              <CommentsPanel
                label="Point of sale (Rx)"
                accent="#0891b2"
                raw={rx.rx_comment_pos as string}
              />
            )}
            {rx.rx_comment_informational && (
              <CommentsPanel
                label="Informational (Rx-level)"
                accent="#6b7280"
                raw={rx.rx_comment_informational as string}
              />
            )}
            {!infoOnly && fcCrit && (
              <CommentsPanel
                label="Fill Notes (this fill)"
                accent="#dc2626"
                raw={fcCrit as string}
              />
            )}
            {!infoOnly && fcPos && (
              <CommentsPanel
                label="Point of sale (this fill)"
                accent="#0891b2"
                raw={fcPos as string}
              />
            )}
            {fcInfo && (
              <CommentsPanel
                label="Informational (this fill)"
                accent="#6b7280"
                raw={fcInfo as string}
              />
            )}
          </div>
        </>
        )
      })()}

      {/* Timeline (replaces the old tabular state log) */}
      {specialCardGate('timeline') !== 'hide' && (<>
      <h2 style={sectionH2}>Timeline</h2>
      {/* D-FIX-1 (Patrick, Rx 61051): the timeline only records OwlRx STATE transitions, so an Rx can
          receive several PioneerRx events — updating its fields — with nothing new shown here. The
          mirror stamps last_mirrored_at on every event, so surface it: a stale state date can no
          longer be misread as "nothing happened". changed_on_utc is PioneerRx's own change stamp. */}
      {/* D-WBFILL-1: on a FILL page the WorkBench entries are narrowed to the ones this fill
          owned, matched by WHEN the change was made — the WorkBench itself has no fill column
          (dbo.rx_workbench is one row per prescription), so the fill is derived rather than
          recorded. Say so, and say where the unfiltered history lives: a derived answer presented
          as a recorded one is the thing this whole fix exists to stop. */}
      {(data as any)?.fill_view && (workbench_log ?? []).length >= 0 ? (
        <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '-6px 0 10px' }}>
          WorkBench entries are matched to this fill by when the change was made. The complete
          history for all fills is on the parent prescription.
          {/* D-WBFILL-3 §2.6: SAY HOW MANY ARE NOT SHOWN. On Rx 104750 three edits were made to one
              field within three hours; this page showed one of them and said nothing about the
              other two, so the work read as lost. The entries belong to other fills and must not
              be listed here — but the count and a route to them must be. */}
          {((data as any).workbench_log_other_fills as number) > 0 && (
            <>
              {' '}
              <a href={`/rx/${rx.id}`}
                 onClick={e => { e.preventDefault(); navigate(`/rx/${rx.id}`) }}
                 style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 600 }}>
                {String((data as any).workbench_log_other_fills)} further WorkBench{' '}
                {((data as any).workbench_log_other_fills as number) === 1 ? 'change belongs' : 'changes belong'}
                {' '}to other fills →
              </a>
            </>
          )}
        </div>
      ) : null}
      {(rx.last_mirrored_at || rx.changed_on_utc) ? (
        <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '-6px 0 10px' }}>
          Last updated from {pmsName}: {formatUsDateTime((rx.last_mirrored_at ?? rx.changed_on_utc) as string)}
          {!isProvider && rx.pioneerrx_rx_status ? <> · current PMS status: {rx.pioneerrx_rx_status as string}</> : null}
        </div>
      ) : null}
      <div style={cardStyle}>
        {mergedTimeline.length === 0 ? (
          <div style={{ padding: '24px', color: 'var(--color-text-mute)', fontSize: 13 }}>
            {(data as any)?.fill_view ? (
              /* D-FILL-1: a fill's timeline holds ONLY its own activity, and it
                 starts at the per-fill tracking deploy. History recorded before
                 fills existed cannot be safely attributed to one fill, so it
                 lives on the parent prescription rather than being guessed here. */
              <>No activity recorded for this fill yet.{' '}
                {((data as any).workbench_log_other_fills as number) > 0 && (
                  <>There {((data as any).workbench_log_other_fills as number) === 1 ? 'is' : 'are'}{' '}
                    <strong>{String((data as any).workbench_log_other_fills)}</strong> WorkBench{' '}
                    {((data as any).workbench_log_other_fills as number) === 1 ? 'change' : 'changes'}{' '}
                    on other fills of this prescription.{' '}</>
                )}
                Earlier history for this prescription (all fills) is on the parent Rx page.</>
            ) : (
              <>No state changes recorded yet. Rxs mirrored from {pmsName} that predate
                timeline tracking may have only their initial state.</>
            )}
          </div>
        ) : (
          <ol style={timelineListStyle}>
            {/* E-CV-203: ONE chronological stream — PMS (state log) + Workbench (audit) — newest
                first, each entry badged by SOURCE with actor + timestamp. */}
            {mergedTimeline.map((ent: any, i: number) => {
              if (ent.kind === 'ship') {
                const ev = ent.ship as api.ShipmentEvent
                const delivered = ev.status === 'delivered'
                const label = ev.carrier_status_text || ev.status.replace(/_/g, ' ')
                return (
                  <li key={`ship-${i}`} style={timelineItemStyle}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', marginTop: 5, flexShrink: 0, background: delivered ? '#16a34a' : '#0369a1' }} aria-hidden="true" />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: '#1e40af', background: '#dbeafe', borderRadius: 4, padding: '1px 6px' }}>DELIVERY</span>
                        <strong style={{ fontSize: 13, color: delivered ? '#166534' : undefined, textTransform: delivered ? undefined : 'capitalize' }}>{label}</strong>
                        {delivered && (
                          <button type="button"
                            onClick={() => { if (id) api.openRxPodDoc(id).catch(() => { alert('Could not open the delivery document. Please try again.') }) }}
                            style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#dcfce7', color: '#166534' }}>
                            View POD ↗
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 4 }}>
                        {formatTimestamp(ev.occurred_at)}{ev.location ? ` · ${ev.location}` : ''}{ev.description && ev.description !== ev.carrier_status_text ? ` · ${ev.description}` : ''}
                      </div>
                    </div>
                  </li>
                )
              }
              if (ent.kind === 'wb') {
                const w = ent.wb!
                const label = (w.field || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
                return (
                  <li key={`wb-${i}`} style={timelineItemStyle}>
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: '#d97706', marginTop: 5 }} aria-hidden="true" />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '1px 6px' }}>WORKBENCH</span>
                        <strong style={{ fontSize: 13 }}>{label}: {w.from ?? '—'} → {w.to ?? '—'}</strong>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 4 }}>
                        {formatTimestamp(w.at)} · by <strong>{w.actor_account ?? 'unknown'}</strong>
                        {w.actor_role ? <span style={mutedSm}> ({w.actor_role})</span> : null}
                      </div>
                    </div>
                  </li>
                )
              }
              const s = ent.pms!
              // Show the verbatim PioneerRx status (chain-of-custody truth); the
              // OwlRx state is our internal remap, kept as a small secondary tag.
              const pxMatch = s.reason ? /PioneerRx status:\s*"?([^"]+?)"?\s*$/i.exec(s.reason) : null
              const px = pxMatch ? pxMatch[1].trim() : null
              const transition = s.from_state ? `${s.from_state} → ${s.to_state}` : `created in ${s.to_state}`
              return (
                <li key={`pms-${i}`} style={timelineItemStyle}>
                  <div style={timelineDotStyle(s.to_state)} aria-hidden="true" />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: '#115e59', background: '#ccfbf1', borderRadius: 4, padding: '1px 6px' }}>PMS</span>
                      <strong style={{ fontSize: 13 }}>{px ?? transition}</strong>
                      <span style={{ ...mutedSm, marginTop: 0 }}>· {px ? transition : s.machine}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 4 }}>
                      {formatTimestamp(s.transitioned_at)} · by <strong>{s.actor_account}</strong>
                      <span style={mutedSm}> ({s.actor_role})</span>
                    </div>
                    {s.reason && !px && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 4, fontStyle: 'italic' }}>
                        {s.reason}
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        )}
      </div>
      </>)}
    </div>
  )
}

// ---------- Comments panel ----------
// PioneerRx packs multiple date-stamped staff notes into a single string,
// e.g. "10/24/2025 4:16 PM FW: ok to leave...10/24/2025 3:15 PM YS: SMS sent...".
// We split on the date prefix so each note renders as its own line.
/** Lucas: NPI display — show real NPI or "Not provided" for GUID fallbacks */
// formatNpi is imported from '../../api/format' to keep behavior consistent
// across all pages that display NPIs.

// =====================================================================================
// D-UI-2 — the three tables of this page, each its own component so its column funnels are
// hooks at the top level of a component (RxDetailPage has early returns and conditional
// sections: a hook added inside it changes the hook count between renders). Markup and styles
// are the page's own, unchanged, plus the funnels and the chip row.
// Each funnel value is what the cell shows; dates stay raw so the funnel compares by day.
// =====================================================================================

const CLAIM_FUNNELS: FunnelColumn<any>[] = [
  { key: 'billing_order', label: 'Order', value: c => String(c.billing_order) },
  { key: 'claim_type', label: 'Type', value: c => c.claim_type },
  { key: 'payor_name', label: 'Payer', value: c => c.payor_name },
  { key: 'bin_pcn', label: 'BIN / PCN', value: c => (c.bin_number ? `${c.bin_number}${c.pcn ? ` / ${c.pcn}` : ''}` : null) },
  { key: 'plan_type', label: 'Plan', value: c => c.plan_type },
  { key: 'transaction_response_status', label: 'Status', value: c => c.transaction_response_status },
  { key: 'transmission_date_utc', label: 'Submitted', value: c => c.transmission_date_utc },
]

function RxClaimsTable({ claims }: { claims: any[] }) {
  const fx = useTableFunnels(claims, CLAIM_FUNNELS)
  return (
    <>
          {fx.chips}
          <table {...fx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Order {fx.funnel('billing_order')}</th>
                <th style={thStyle}>Type {fx.funnel('claim_type')}</th>
                <th style={thStyle}>Payer {fx.funnel('payor_name')}</th>
                <th style={thStyle}>BIN / PCN {fx.funnel('bin_pcn')}</th>
                <th style={thStyle}>Plan {fx.funnel('plan_type')}</th>
                <th style={thStyle}>Status {fx.funnel('transaction_response_status')}</th>
                <th style={thStyle}>Submitted {fx.funnel('transmission_date_utc')}</th>
              </tr>
            </thead>
            <tbody>
              {fx.rows.map((c: any, i: number) => (
                <tr key={i} style={trStyle}>
                  <td style={tdStyle}>{String(c.billing_order)}</td>
                  <td style={tdStyle}>{c.claim_type as string}</td>
                  <td style={tdStyle}>{(c.payor_name as string) ?? '—'}</td>
                  <td style={tdStyle}>{c.bin_number ? `${c.bin_number}${c.pcn ? ` / ${c.pcn}` : ''}` : '—'}</td>
                  <td style={tdStyle}>{(c.plan_type as string) ?? '—'}</td>
                  <td style={tdStyle}>{(c.transaction_response_status as string) ?? '—'}</td>
                  <td style={tdStyle}>{c.transmission_date_utc ? formatTimestamp(c.transmission_date_utc as string) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
    </>
  )
}

const FILL_PAGE_FUNNELS: FunnelColumn<api.RxFillPage>[] = [
  { key: 'display_rx_number', label: 'Rx', value: fp => fp.display_rx_number },
  { key: 'fill_number', label: 'Fill', value: fp => fp.fill_number },
  { key: 'status', label: 'Status', value: fp => fp.pioneerrx_tx_status ?? fp.state },
  { key: 'date_filled_utc', label: 'Filled', value: fp => fp.date_filled_utc },
  { key: 'quantity_dispensed', label: 'Qty', value: fp => fp.quantity_dispensed },
  { key: 'days_supply', label: 'Days', value: fp => fp.days_supply },
]

/** `thisPageRx` = the display number of the fill being viewed (undefined on a prescription page). */
function RxFillPagesTable({ fillPages, thisPageRx }: { fillPages: api.RxFillPage[]; thisPageRx?: string }) {
  const navigate = useNavigate()
  const fx = useTableFunnels(fillPages, FILL_PAGE_FUNNELS)
  return (
    <>
        {fx.chips}
        <table {...fx.tableProps} style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Rx {fx.funnel('display_rx_number')}</th>
              <th style={thStyle}>Fill {fx.funnel('fill_number')}</th>
              <th style={thStyle}>Status {fx.funnel('status')}</th>
              <th style={thStyle}>Filled {fx.funnel('date_filled_utc')}</th>
              <th style={thStyle}>Qty {fx.funnel('quantity_dispensed')}</th>
              <th style={thStyle}>Days {fx.funnel('days_supply')}</th>
            </tr>
          </thead>
          <tbody>
            {fx.rows.map(fp => (
              <tr key={fp.id}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>
                  {/* D-FILL-5: mark the row you are already on, so clicking it and
                      appearing not to move is explained rather than confusing. */}
                  {thisPageRx === fp.display_rx_number ? (
                    <span style={{ color: 'var(--color-text)' }}>
                      {fp.display_rx_number}{' '}
                      <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>(this page)</span>
                    </span>
                  ) : (
                    <a href={`/rx/${fp.id}`}
                       onClick={e => { e.preventDefault(); navigate(`/rx/${fp.id}`) }}
                       style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
                      {fp.display_rx_number}
                    </a>
                  )}
                  {fp.fill_number === 0 && (
                    <span style={{ marginLeft: 8, color: 'var(--color-text-mute)', fontWeight: 400, fontSize: 12 }}>
                      original
                    </span>
                  )}
                </td>
                <td style={tdStyle}>{fp.fill_number}</td>
                <td style={tdStyle}>{fp.pioneerrx_tx_status ?? fp.state ?? '—'}</td>
                <td style={tdStyle}>{fp.date_filled_utc ? new Date(fp.date_filled_utc).toLocaleDateString() : '—'}</td>
                <td style={tdStyle}>{fp.quantity_dispensed ?? '—'}</td>
                <td style={tdStyle}>{fp.days_supply ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
    </>
  )
}

const FILL_FUNNELS: FunnelColumn<any>[] = [
  { key: 'fill_number', label: '#', value: f => f.fill_number },
  { key: 'quantity_filled', label: 'Quantity', value: f => f.quantity_filled },
  { key: 'days_supply', label: 'Days supply', value: f => f.days_supply },
  { key: 'lot_number', label: 'Lot', value: f => f.lot_number },
  { key: 'filled_by_account', label: 'Filled by', value: f => f.filled_by_account },
  { key: 'filled_at', label: 'Filled at', value: f => f.filled_at },
  { key: 'status', label: 'Status', value: f => (f.is_reversed ? `Reversed${f.reversed_reason ? ` (${f.reversed_reason})` : ''}` : 'Active') },
]

function RxFillsTable({ fills }: { fills: any[] }) {
  const fx = useTableFunnels(fills, FILL_FUNNELS)
  return (
    <>
          {fx.chips}
          <table {...fx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}># {fx.funnel('fill_number')}</th>
                <th style={thStyle}>Quantity {fx.funnel('quantity_filled')}</th>
                <th style={thStyle}>Days supply {fx.funnel('days_supply')}</th>
                <th style={thStyle}>Lot {fx.funnel('lot_number')}</th>
                <th style={thStyle}>Filled by {fx.funnel('filled_by_account')}</th>
                <th style={thStyle}>Filled at {fx.funnel('filled_at')}</th>
                <th style={thStyle}>Status {fx.funnel('status')}</th>
              </tr>
            </thead>
            <tbody>
              {fx.rows.map((f: any) => (
                <tr key={f.id} style={trStyle}>
                  <td style={tdStyle}>{f.fill_number}</td>
                  <td style={tdStyle}>{f.quantity_filled}</td>
                  <td style={tdStyle}>{f.days_supply ?? '—'}</td>
                  <td style={tdStyle}>{f.lot_number ?? '—'}</td>
                  <td style={tdStyle}>{f.filled_by_account}</td>
                  <td style={tdStyle}>{formatTimestamp(f.filled_at)}</td>
                  <td style={tdStyle}>
                    {f.is_reversed
                      ? <span style={{ color: '#991b1b' }}>Reversed{f.reversed_reason ? ` (${f.reversed_reason})` : ''}</span>
                      : 'Active'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
    </>
  )
}

function CommentsPanel({ label, accent, raw }: { label: string; accent: string; raw: string }) {
  // Split before each date-prefix. Matches MM/DD/YYYY HH:MM AM/PM.
  // Keep the delimiter via lookahead so it stays at the start of each entry.
  const datePrefix = /(?=\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}\s*(?:AM|PM))/g
  const entries = raw.split(datePrefix).map(s => s.trim()).filter(Boolean)
  return (
    <div style={{
      background: 'var(--color-surface, #fff)',
      border: '1px solid var(--color-border)',
      borderLeft: `4px solid ${accent}`,
      borderRadius: 6,
      padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
        textTransform: 'uppercase', color: accent, marginBottom: 8
      }}>{label}</div>
      {entries.length <= 1 ? (
        <div style={{ fontSize: 13, lineHeight: 1.45 }}>{raw}</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
          {entries.map((e, i) => {
            const m = e.match(/^(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}\s*(?:AM|PM))\s+([A-Z]{1,4}):\s*(.*)$/)
            if (m) {
              return (
                <li key={i} style={{ fontSize: 13, lineHeight: 1.45 }}>
                  <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>{m[1]}</span>
                  <span style={{ marginLeft: 8, fontWeight: 600 }}>{m[2]}:</span>
                  <span style={{ marginLeft: 4 }}>{m[3]}</span>
                </li>
              )
            }
            return <li key={i} style={{ fontSize: 13, lineHeight: 1.45 }}>{e}</li>
          })}
        </ul>
      )}
    </div>
  )
}

// ----- Sub-components -----

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <header style={sectionHeader}>
        <Icon size={14} aria-hidden="true" />
        <h3 style={sectionH3}>{title}</h3>
      </header>
      <div style={{ padding: '12px 16px 16px' }}>{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={fieldRow}>
      <div style={fieldLabel}>{label}</div>
      <div style={fieldValue}>{children || '—'}</div>
    </div>
  )
}

/** D-SOC-1 — Site of care rows on the Patient card. Own component so the query hook sits below the
 *  page's loading/error early returns without changing the page's hook count. */
function RxSiteOfCareFields({ rxId }: { rxId: string }) {
  const q = useRxSiteOfCare(rxId)
  if (q.isLoading) return <Field label="Site of care"><span style={{ color: 'var(--color-text-muted)' }}>Checking address…</span></Field>
  if (q.isError) return <Field label="Site of care"><span style={{ color: '#b91c1c' }}>Could not resolve — {friendlyError(q.error)}</span></Field>
  const soc = q.data?.site_of_care
  if (!q.data?.available || !soc) return null
  const residential = soc.facility_type === 'residential'
  const typeShort = soc.facility_type_label.split(' — ')[0]
  return (
    <>
      <Field label="Site of care">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11.5, padding: '2px 9px', borderRadius: 20, background: residential ? '#F1EFE8' : '#fef3c7', color: residential ? '#444441' : '#92400e', fontWeight: 600 }}>{typeShort}</span>
          {soc.match_method === 'manual' && <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>pinned by pharmacy</span>}
          {soc.match_method === 'address_loose' && <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }} title="Number + street + zip matched; suffix differed">near match</span>}
        </span>
      </Field>
      {!residential && <Field label="Facility">{soc.facility_name}{soc.facility_address ? <span style={{ color: 'var(--color-text-muted)' }}> · {soc.facility_address}</span> : null}</Field>}
      {!residential && <Field label="Facility phone">{formatPhone(soc.facility_phone)}</Field>}
      {!residential && soc.contact_name && <Field label="Facility contact">{soc.contact_name}{soc.contact_phone ? ` · ${formatPhone(soc.contact_phone)}` : ''}{soc.contact_email ? ` · ${soc.contact_email}` : ''}</Field>}
      {!residential && soc.operating_hours && <Field label="Operating hours">{soc.operating_hours}</Field>}
      {!residential && soc.delivery_cutoff_time && <Field label="Delivery cutoff">{soc.delivery_cutoff_time}</Field>}
    </>
  )
}

function Money({ label, v }: { label: string; v: any }) {
  if (v == null) return <Field label={label}>—</Field>
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n)) return <Field label={label}>—</Field>
  return <Field label={label}>${n.toFixed(2)}</Field>
}

function ProfitSection({ rx }: { rx: any }) {
  const acq    = numOr0(rx.acquisition_cost)
  const fee    = numOr0(rx.dispensing_fee_paid)
  const totRev = numOr0(rx.total_price_paid)
  const patP   = numOr0(rx.patient_paid_amount)
  const insP   = Math.max(0, totRev - patP)
  const cost   = acq + fee
  const profit = totRev - cost
  const margin = totRev > 0 ? (profit / totRev) * 100 : 0

  // No data yet — skip the whole section so we don't show $0 / $0 misleadingly
  if (acq === 0 && totRev === 0) return null

  const profitTone = profit >= 0 ? '#15803d' : '#dc2626'
  const marginTone = margin >= 30 ? '#15803d' : margin >= 15 ? 'var(--color-text)' : '#a16207'

  return (
    <Section icon={TrendingUp} title="Profit">
      <Field label="Total revenue (paid)">${totRev.toFixed(2)}</Field>
      <Field label="Insurance portion">${insP.toFixed(2)}</Field>
      <Field label="Patient portion">${patP.toFixed(2)}</Field>
      <Field label="Total cost (acq + fee)">${cost.toFixed(2)}</Field>
      <Field label="Profit">
        <span style={{ color: profitTone, fontWeight: 600 }}>${profit.toFixed(2)}</span>
      </Field>
      <Field label="Margin">
        <span style={{ color: marginTone, fontWeight: 600 }}>{margin.toFixed(1)}%</span>
      </Field>
    </Section>
  )
}

function numOr0(v: any): number {
  if (v == null) return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isNaN(n) ? 0 : n
}

// ----- Styles -----

const backBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 10px',
  background: 'transparent',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--color-text-soft)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginBottom: 16,
}
const openLinkBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px',
  background: 'transparent',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  fontSize: 12, fontWeight: 500,
  color: 'var(--color-text-soft)',
  cursor: 'pointer',
  fontFamily: 'inherit',
}
const headerCard: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 20,
  marginBottom: 16,
}
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const mutedSm: React.CSSProperties = {
  fontSize: 12, color: 'var(--color-text-mute)',
}
const controlledBadge: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
  padding: '2px 8px',
  background: '#fef3c7',
  color: '#854d0e',
  borderRadius: 4,
}
const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))',
  gap: 16,
  marginBottom: 16,
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  overflow: 'hidden',
  marginBottom: 16,
}
const sectionHeader: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 16px',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-alt)',
  color: 'var(--color-text-soft)',
}
const sectionH3: React.CSSProperties = {
  margin: 0, fontSize: 12, fontWeight: 700,
  letterSpacing: 0.5, textTransform: 'uppercase',
}
const sectionH2: React.CSSProperties = {
  fontSize: 16, fontWeight: 600, margin: '8px 0 12px',
  fontFamily: 'Georgia, serif',
}
const fieldRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '140px 1fr',
  gap: 10,
  padding: '4px 0',
  fontSize: 13,
  borderBottom: '1px solid #f5f5f4',
}
const fieldLabel: React.CSSProperties = {
  color: 'var(--color-text-mute)',
  fontSize: 12,
}
const fieldValue: React.CSSProperties = {
  color: 'var(--color-text)',
  wordBreak: 'break-word',
}
const hrStyle: React.CSSProperties = {
  border: 'none', borderTop: '1px dashed var(--color-border)', margin: '10px 0',
}
const alertBoxStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid',
  borderRadius: 4,
  fontSize: 13,
  marginBottom: 8,
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = { borderBottom: '1px solid var(--color-border)' }
const tdStyle: React.CSSProperties = { padding: '10px 14px' }

// WI-908: timeline + original-status display
const originalStatusStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--color-text-mute)',
  background: 'rgba(0,0,0,0.04)',
  padding: '2px 8px',
  borderRadius: 999,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
}
const timelineListStyle: React.CSSProperties = {
  listStyle: 'none', padding: '16px 20px', margin: 0,
  display: 'grid', gap: 14,
}
const timelineItemStyle: React.CSSProperties = {
  display: 'flex', gap: 12, alignItems: 'flex-start',
  paddingLeft: 4,
}
function timelineDotStyle(state: string): React.CSSProperties {
  const colors: Record<string, string> = {
    submitted: '#f59e0b',
    verified: '#10b981',
    ready_to_fill: '#3b82f6',
    dispensed: '#8b5cf6',
    out_for_delivery: '#06b6d4',
    delivered: '#22c55e',
    on_hold: '#eab308',
    cancelled: '#9ca3af',
    rejected: '#ef4444',
  }
  return {
    width: 10, height: 10, borderRadius: '50%',
    marginTop: 5, flexShrink: 0,
    background: colors[state] ?? '#9ca3af',
    boxShadow: '0 0 0 3px var(--color-surface, #fff), 0 0 0 4px var(--color-border)',
  }
}
